import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { DateFormat, formatDate } from "@intentsify/utils";
import { EmptyCell } from "components/Table/components/EmptyCell";
import { useComponentColors } from "theme";
import { UserActivityLogTableRow } from "./UserAuditLog.columns";

type UserAuditLogDetailsDrawerProps = {
  activityLog: UserActivityLogTableRow | null;
  isOpen: boolean;
  onClose: () => void;
};

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) => (
  <Box>
    <Text fontSize="xs" fontWeight="semibold" color="gray.500" mb={1}>
      {label}
    </Text>
    <Text>{value ? value : <EmptyCell />}</Text>
  </Box>
);

const formatLoggedAt = (loggedAt?: string) =>
  loggedAt
    ? formatDate({
        date: new Date(loggedAt),
        format: DateFormat.DateTime,
      })
    : "";

const formatActivityParamsForDetails = (
  activityParams: Record<string, unknown> | null,
) =>
  activityParams && Object.keys(activityParams).length > 0
    ? JSON.stringify(activityParams, null, 2)
    : "";

const UserAuditLogDetailsDrawer = ({
  activityLog,
  isOpen,
  onClose,
}: UserAuditLogDetailsDrawerProps) => {
  const {
    form: { content, stackBg },
  } = useComponentColors();

  const formattedActivityParams = activityLog
    ? formatActivityParamsForDetails(activityLog.activityParams)
    : "";

  return (
    <Drawer size="md" isOpen={isOpen} onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent bg={content}>
        <DrawerCloseButton />
        <DrawerHeader bg={stackBg}>Activity details</DrawerHeader>
        <DrawerBody>
          {activityLog && (
            <Stack spacing={4}>
              <DetailField
                label="Logged At"
                value={formatLoggedAt(activityLog.loggedAt)}
              />
              <DetailField label="User" value={activityLog.userDisplayName} />
              <DetailField label="Email" value={activityLog.userEmail} />
              <DetailField label="Company" value={activityLog.companyName} />
              <DetailField
                label="Activity Type"
                value={activityLog.activityName}
              />

              <Box>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  mb={1}
                >
                  Activity Params
                </Text>
                {formattedActivityParams ? (
                  <Box
                    as="pre"
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    fontSize="sm"
                    overflowX="auto"
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {formattedActivityParams}
                  </Box>
                ) : (
                  <EmptyCell />
                )}
              </Box>
            </Stack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export { UserAuditLogDetailsDrawer, formatActivityParamsForDetails };
